import React, { useState, useEffect, useCallback } from 'react';
import { X, Award, Gamepad2, Film, Lock, CheckCircle2, Globe2 } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import axios from 'axios';
import { API_URL } from '../config/api';

const BADGE_EMOJIS = {
  gamepad: '\ud83c\udfae', flame: '\ud83d\udd25', target: '\ud83c\udfaf', trophy: '\ud83c\udfc6',
  swords: '\u2694\ufe0f', film: '\ud83c\udfac', clapperboard: '\ud83c\udfac', crown: '\ud83d\udc51',
  pen: '\u270f\ufe0f', users: '\ud83d\udc65', book: '\ud83d\udcd6', star: '\u2b50',
  tv: '\ud83d\udcfa', skull: '\ud83d\udc80', rocket: '\ud83d\ude80', laugh: '\ud83d\ude02',
  theater: '\ud83c\udfad', zap: '\u26a1', globe: '\ud83c\udf0d',
  sofa: '\ud83d\udecb\ufe0f', flashlight: '\ud83d\udd26', flask: '\u2697\ufe0f', dragon: '\ud83d\udc09',
  heart: '\u2764\ufe0f', zombie: '\ud83e\udddf', mirror: '\ud83e\ude9e', castle: '\ud83c\udff0',
  wind: '\ud83c\udf2c\ufe0f', medal: '\ud83c\udf96\ufe0f',
};

const BadgeCard = ({ badge }) => {
  const emoji = BADGE_EMOJIS[badge.icon] || '\ud83c\udfc5';
  const earned = badge.earned;
  const hasProgress = badge.target && badge.target > 0;
  const progressPct = hasProgress ? Math.min((badge.progress || 0) / badge.target * 100, 100) : 0;

  return (
    <div
      className={`relative rounded-xl p-3 sm:p-4 text-center transition-all ${
        earned
          ? 'bg-gradient-to-b from-zinc-800 to-zinc-800/50 border border-amber-800/40 shadow-lg shadow-amber-900/10'
          : 'bg-zinc-800/50 border border-zinc-700/50'
      }`}
      data-testid={`badge-${badge.id}`}
    >
      <div className={`w-12 h-12 sm:w-14 sm:h-14 mx-auto rounded-full flex items-center justify-center mb-1.5 sm:mb-2 ${
        earned ? '' : 'grayscale opacity-40'
      }`}>
        <span className="text-2xl sm:text-3xl" role="img">{emoji}</span>
      </div>
      <h4 className={`text-[11px] sm:text-sm font-semibold leading-tight ${earned ? 'text-white' : 'text-gray-400'}`}>
        {badge.name}
      </h4>
      <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 leading-tight">{badge.description}</p>

      {hasProgress && !earned && (
        <div className="mt-2">
          <div className="w-full h-1 bg-zinc-700 rounded-full overflow-hidden">
            <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
          </div>
          <p className="text-[9px] text-gray-500 mt-0.5">{badge.progress || 0}/{badge.target}</p>
        </div>
      )}

      {earned && badge.earned_at && (
        <div className="flex items-center justify-center gap-1 mt-1.5 text-[10px] sm:text-xs text-amber-500">
          <CheckCircle2 className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
          <span>{new Date(badge.earned_at).toLocaleDateString()}</span>
        </div>
      )}
    </div>
  );
};

const TABS = [
  { key: 'movie', label: 'Movies', icon: Film, color: 'red' },
  { key: 'genre', label: 'Genres', icon: Globe2, color: 'green' },
  { key: 'game', label: 'Games', icon: Gamepad2, color: 'amber' },
];

const Badges = ({ user, isOpen, onClose }) => {
  const [badges, setBadges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('movie');

  const fetchBadges = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/badges/${user.id}`);
      setBadges(res.data);
    } catch (err) {
      console.error('Failed to fetch badges:', err);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    if (isOpen) fetchBadges();
  }, [isOpen, fetchBadges]);

  const currentBadges = activeTab === 'game'
    ? (badges?.game_badges || [])
    : activeTab === 'genre'
      ? (badges?.genre_badges || [])
      : (badges?.movie_badges || []);
  const earnedCount = currentBadges.filter(b => b.earned).length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-lg bg-zinc-900 border-zinc-700 text-white p-0 max-h-[88vh] overflow-y-auto rounded-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 sticky top-0 bg-zinc-900 z-10">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <Award className="h-4 w-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold leading-tight" data-testid="badges-title">Badges</h2>
              <p className="text-[11px] text-gray-400">{badges?.total_earned || 0} / {badges?.total_available || 0} earned</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-zinc-700 text-gray-400" data-testid="badges-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* 3-Category Tabs */}
        <div className="flex border-b border-zinc-700">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            const borderColor = tab.color === 'red' ? 'border-red-500' : tab.color === 'green' ? 'border-green-500' : 'border-amber-500';
            const textColor = tab.color === 'red' ? 'text-red-400' : tab.color === 'green' ? 'text-green-400' : 'text-amber-400';
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors ${
                  isActive ? `${borderColor} ${textColor}` : 'border-transparent text-gray-400 hover:text-white'
                }`}
                data-testid={`badges-tab-${tab.key}`}
              >
                <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Progress Bar */}
        <div className="px-4 pt-2.5">
          <div className="flex items-center justify-between text-[10px] sm:text-xs text-gray-400 mb-1">
            <span>{earnedCount} earned</span>
            <span>{currentBadges.length} total</span>
          </div>
          <div className="w-full h-1.5 sm:h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-500"
              style={{ width: `${currentBadges.length > 0 ? (earnedCount / currentBadges.length) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* Badge Grid */}
        <div className="p-3 sm:p-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin h-7 w-7 border-2 border-purple-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3" data-testid="badges-grid">
              {currentBadges.map(badge => (
                <BadgeCard key={badge.id} badge={badge} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Badges;
